import { lazy, Suspense, useEffect, type ComponentType } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/AuthContext';
import { AppShell } from '@/layout/AppShell';
import { LoginPage } from '@/pages/LoginPage';
import { ChangePasswordPage } from '@/pages/ChangePasswordPage';
import { MASTER_CONFIGS } from '@/pages/masters.config';
import { PageFallback } from '@/components/ErrorBoundary';
import { onScreenChange } from '@/pwa';

/**
 * One chunk per screen, so the first load is the shell and the page asked
 * for rather than all fifty. Pages are named exports; lazy() wants a default.
 */
function lazyPage<M extends Record<K, ComponentType<any>>, K extends keyof M & string>(
  load: () => Promise<M>,
  name: K,
) {
  return lazy(() => load().then((module) => ({ default: module[name] })));
}

const ApprovalsPage = lazyPage(() => import('@/pages/ApprovalsPage'), 'ApprovalsPage');
const AttendancePage = lazyPage(() => import('@/pages/AttendancePage'), 'AttendancePage');
const CompliancePage = lazyPage(() => import('@/pages/CompliancePage'), 'CompliancePage');
const DeviceOpsPage = lazyPage(() => import('@/pages/DeviceOpsPage'), 'DeviceOpsPage');
const ApplicantsPage = lazyPage(() => import('@/pages/ApplicantsPage'), 'ApplicantsPage');
const EmployeesPage = lazyPage(() => import('@/pages/EmployeesPage'), 'EmployeesPage');
const EmployeeProfilePage = lazyPage(() => import('@/pages/EmployeeProfilePage'), 'EmployeeProfilePage');
const FleetPage = lazyPage(() => import('@/pages/FleetPage'), 'FleetPage');
const GrievancePage = lazyPage(() => import('@/pages/GrievancePage'), 'GrievancePage');
const PerformancePage = lazyPage(() => import('@/pages/PerformancePage'), 'PerformancePage');
const RecruitmentPage = lazyPage(() => import('@/pages/RecruitmentPage'), 'RecruitmentPage');
const TrainingPage = lazyPage(() => import('@/pages/TrainingPage'), 'TrainingPage');
const TravelPage = lazyPage(() => import('@/pages/TravelPage'), 'TravelPage');
const WorkplacePage = lazyPage(() => import('@/pages/WorkplacePage'), 'WorkplacePage');
const ExpensesPage = lazyPage(() => import('@/pages/ExpensesPage'), 'ExpensesPage');
const LeavePage = lazyPage(() => import('@/pages/LeavePage'), 'LeavePage');
const PayrollPage = lazyPage(() => import('@/pages/PayrollPage'), 'PayrollPage');
const BenefitsPage = lazyPage(() => import('@/pages/BenefitsPage'), 'BenefitsPage');
const SalaryStructuresPage = lazyPage(() => import('@/pages/SalaryStructuresPage'), 'SalaryStructuresPage');
const PayrollSettingsPage = lazyPage(() => import('@/pages/PayrollSettingsPage'), 'PayrollSettingsPage');
const LifecyclePage = lazyPage(() => import('@/pages/LifecyclePage'), 'LifecyclePage');
const ExitPage = lazyPage(() => import('@/pages/ExitPage'), 'ExitPage');
const ReportsPage = lazyPage(() => import('@/pages/ReportsPage'), 'ReportsPage');
const SalarySlipsPage = lazyPage(() => import('@/pages/SalarySlipsPage'), 'SalarySlipsPage');
const ShiftAssignmentsPage = lazyPage(() => import('@/pages/ShiftAssignmentsPage'), 'ShiftAssignmentsPage');
const WeeklyRosterPage = lazyPage(() => import('@/pages/WeeklyRosterPage'), 'WeeklyRosterPage');
const BranchesPage = lazyPage(() => import('@/pages/BranchesPage'), 'BranchesPage');
const CompaniesPage = lazyPage(() => import('@/pages/CompaniesPage'), 'CompaniesPage');
const DashboardPage = lazyPage(() => import('@/pages/DashboardPage'), 'DashboardPage');
const GuidePage = lazyPage(() => import('@/pages/GuidePage'), 'GuidePage');
const DepartmentsPage = lazyPage(() => import('@/pages/DepartmentsPage'), 'DepartmentsPage');
const HolidayListsPage = lazyPage(() => import('@/pages/HolidayListsPage'), 'HolidayListsPage');
const JobsPage = lazyPage(() => import('@/pages/JobsPage'), 'JobsPage');
const DataPage = lazyPage(() => import('@/pages/DataPage'), 'DataPage');
const SystemLogsPage = lazyPage(() => import('@/pages/SystemLogsPage'), 'SystemLogsPage');
const WorkflowPage = lazyPage(() => import('@/pages/WorkflowPage'), 'WorkflowPage');
const StatutoryPage = lazyPage(() => import('@/pages/StatutoryPage'), 'StatutoryPage');
const BrandingPage = lazyPage(() => import('@/pages/BrandingPage'), 'BrandingPage');
const NotificationRulesPage = lazyPage(() => import('@/pages/NotificationRulesPage'), 'NotificationRulesPage');
const MasterListPage = lazyPage(() => import('@/pages/MasterListPage'), 'MasterListPage');
const NotificationsPage = lazyPage(() => import('@/pages/NotificationsPage'), 'NotificationsPage');
const PermissionsPage = lazyPage(() => import('@/pages/PermissionsPage'), 'PermissionsPage');
const RolesPage = lazyPage(() => import('@/pages/RolesPage'), 'RolesPage');
const UsersPage = lazyPage(() => import('@/pages/UsersPage'), 'UsersPage');


function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading, sessionError, retrySession, mustChangePassword } = useAuth();
  const { t } = useTranslation();
  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  // Still signed in, just unable to reach the server: say so rather than
  // showing the sign-in form, which would suggest the session was lost.
  if (!user && sessionError) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100vh', p: 2 }}>
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={retrySession}>
              {t('Retry')}
            </Button>
          }
        >
          {t('Could not reach the server. Check the connection and try again.')}
        </Alert>
      </Box>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // The server refuses everything else until the account picks its own password.
  if (mustChangePassword) return <ChangePasswordPage />;
  return <>{children}</>;
}

/** Renders any config-driven master from a single route. */
function MasterRoute() {
  const { master = '' } = useParams();
  const config = MASTER_CONFIGS[master];
  if (!config) return <Navigate to="/" replace />;
  return (
    <MasterListPage
      key={master}
      doctype={config.doctype}
      endpoint={config.endpoint}
      titleKey={config.titleKey}
      columns={config.columns}
      fields={config.fields}
    />
  );
}

export function App() {
  // A release that arrived while this screen was open is taken on the way to
  // the next one (see pwa.ts).
  const { pathname } = useLocation();
  useEffect(() => onScreenChange(), [pathname]);

  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route path="/" element={<DashboardPage />} />
          <Route path="/approvals" element={<ApprovalsPage />} />
          <Route path="/guide" element={<GuidePage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/employees" element={<EmployeesPage />} />
          <Route path="/employees/:id" element={<EmployeeProfilePage />} />
          <Route path="/compliance" element={<CompliancePage />} />
          <Route path="/attendance" element={<AttendancePage />} />
          <Route path="/shift-assignments" element={<ShiftAssignmentsPage />} />
          <Route path="/weekly-roster" element={<WeeklyRosterPage />} />
          <Route path="/devices" element={<DeviceOpsPage />} />
          <Route path="/leave" element={<LeavePage />} />
          <Route path="/expenses" element={<ExpensesPage />} />
          <Route path="/payroll" element={<PayrollPage />} />
          <Route path="/salary-slips" element={<SalarySlipsPage />} />
          <Route path="/recruitment" element={<RecruitmentPage />} />
          <Route path="/applicants" element={<ApplicantsPage />} />
          <Route path="/performance" element={<PerformancePage />} />
          <Route path="/training" element={<TrainingPage />} />
          <Route path="/fleet" element={<FleetPage />} />
          <Route path="/travel" element={<TravelPage />} />
          <Route path="/grievances" element={<GrievancePage />} />
          <Route path="/workplace" element={<WorkplacePage />} />
          <Route path="/salary-structures" element={<SalaryStructuresPage />} />
          <Route path="/payroll-settings" element={<PayrollSettingsPage />} />
          <Route path="/lifecycle" element={<LifecyclePage />} />
          <Route path="/exit" element={<ExitPage />} />
          <Route path="/benefits" element={<BenefitsPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/companies" element={<CompaniesPage />} />
          <Route path="/branches" element={<BranchesPage />} />
          <Route path="/departments" element={<DepartmentsPage />} />
          <Route path="/holiday-lists" element={<HolidayListsPage />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/roles" element={<RolesPage />} />
          <Route path="/permissions" element={<PermissionsPage />} />
          <Route path="/jobs" element={<JobsPage />} />
          <Route path="/data" element={<DataPage />} />
          <Route path="/system-logs" element={<SystemLogsPage />} />
          <Route path="/workflows" element={<WorkflowPage />} />
          <Route path="/statutory" element={<StatutoryPage />} />
          <Route path="/branding" element={<BrandingPage />} />
          <Route path="/notification-rules" element={<NotificationRulesPage />} />
          {/* designations, employee-grades, employment-types, administrations, nationalities */}
          <Route path="/:master" element={<MasterRoute />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
